import React, { useState, useEffect } from 'react';
import { 
  X, Send, Loader2, CheckCircle, FileText, 
  Package, Ruler, Clock, Palette, ShoppingBag,
  Plus, Minus, ChevronRight, AlertCircle, 
  MessageSquare, Image as ImageIcon
} from 'lucide-react';
import { db } from '../../services/firebase';
import { 
  doc, collection, query, where, getDocs, 
  addDoc, serverTimestamp, updateDoc, increment, setDoc
} from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';

// ============================================================================
// TYPES
// ============================================================================

interface DesignProject {
  id: string;
  name: string;
  image: string;
  category: string;
  colors?: string[];
  sizes?: string[];
  moq?: number;
  leadTime?: string;
  description?: string;
}

interface Manufacturer {
  id: string;
  businessName: string;
  displayName?: string;
  photoURL?: string;
  logoUrl?: string;
  location?: string;
  category?: string[];
  moq?: number;
  leadTime?: string;
}

interface EnquiryTemplate {
  id: string;
  question: string;
  defaultText?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const QUICK_QUESTIONS: EnquiryTemplate[] = [
  { id: 'moq', question: 'What is your minimum order quantity (MOQ) for this design?', defaultText: 'What is your MOQ?' },
  { id: 'lead_time', question: 'What is the production lead time for this quantity?', defaultText: 'What is your lead time?' },
  { id: 'samples', question: 'Can you produce samples before bulk production?', defaultText: 'Can I order samples?' },
  { id: 'fabrics', question: 'What fabric/materials options are available for this design?', defaultText: 'What fabrics are available?' },
  { id: 'colors', question: 'Can you customize the colors for this design?', defaultText: 'Custom colors available?' },
  { id: 'bulk_pricing', question: 'What are the bulk pricing tiers? (e.g. 50, 100, 500, 1000+)', defaultText: 'Bulk pricing?' },
  { id: 'shipping', question: 'What are the shipping costs and timelines to my location?', defaultText: 'Shipping costs?' },
  { id: 'tech_pack', question: 'Do you need a tech pack or is the design image sufficient?', defaultText: 'Tech pack needed?' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface EnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  manufacturer: Manufacturer | null;
  onEnquirySent?: () => void;
}

export const EnquiryModal: React.FC<EnquiryModalProps> = ({
  isOpen,
  onClose,
  manufacturer,
  onEnquirySent,
}) => {
  const { user: currentUser, profile: userProfile } = useAuth();

  // ============================================================================
  // STATE
  // ============================================================================
  
  const [designs, setDesigns] = useState<DesignProject[]>([]);
  const [loadingDesigns, setLoadingDesigns] = useState(true);
  const [selectedDesign, setSelectedDesign] = useState<DesignProject | null>(null);
  
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});
  
  const [quantity, setQuantity] = useState(50);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // FETCH DESIGNS
  // ============================================================================
  
  useEffect(() => {
    if (!isOpen || !currentUser) return;

    const fetchDesigns = async () => {
      setLoadingDesigns(true);
      try {
        // Fetch from products collection (where designer saves designs)
        const q = query(
          collection(db, 'products'),
          where('businessId', '==', currentUser.uid),
          where('type', '==', 'design_project')
        );
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as DesignProject[];
        setDesigns(data);
      } catch (error) {
        console.error('Error fetching designs:', error);
      } finally {
        setLoadingDesigns(false);
      }
    };

    fetchDesigns();
  }, [isOpen, currentUser]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const toggleQuestion = (question: string) => {
    setSelectedQuestions(prev =>
      prev.includes(question)
        ? prev.filter(q => q !== question)
        : [...prev, question]
    );
  };

  const handleCustomAnswerChange = (questionId: string, value: string) => {
    setCustomAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSendEnquiry = async () => {
    if (!currentUser || !manufacturer) return;

    // Validate
    if (!selectedDesign) {
      setError('Please select a design to enquire about.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const otherId = manufacturer.id;
      const chatId = [currentUser.uid, otherId].sort().join('_');

      // Build enquiry message
      let enquiryText = `📐 **Design Enquiry**\n\n`;
      enquiryText += `**Design:** ${selectedDesign.name}\n`;
      enquiryText += `**Quantity:** ${quantity} units\n`;
      
      if (selectedColor) enquiryText += `**Color:** ${selectedColor}\n`;
      if (selectedSize) enquiryText += `**Size:** ${selectedSize}\n`;
      
      enquiryText += `\n**Questions:**\n`;
      
      // Add selected quick questions
      selectedQuestions.forEach(q => {
        const template = QUICK_QUESTIONS.find(t => t.question === q);
        if (template) {
          enquiryText += `• ${q}\n`;
        }
      });

      // Add custom questions and answers
      Object.entries(customAnswers).forEach(([id, answer]) => {
        if (answer.trim()) {
          const question = QUICK_QUESTIONS.find(q => q.id === id);
          if (question) {
            enquiryText += `• ${question.question}\n  → ${answer}\n`;
          }
        }
      });

      if (notes.trim()) {
        enquiryText += `\n**Additional Notes:**\n${notes}\n`;
      }

      // Create or update chat
      await setDoc(doc(db, 'chats', chatId), {
        participants: [currentUser.uid, otherId],
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || 'Designer',
          [otherId]: manufacturer.businessName || manufacturer.displayName || 'Manufacturer',
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [otherId]: manufacturer.photoURL || manufacturer.logoUrl || '',
        },
        hidden: {
          [currentUser.uid]: false,
          [otherId]: false,
        },
        updatedAt: serverTimestamp(),
        [`unreadCount.${otherId}`]: increment(1),
      }, { merge: true });

      // Send message with design attachment
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: enquiryText,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'design_enquiry',
        designProject: {
          id: selectedDesign.id,
          name: selectedDesign.name,
          image: selectedDesign.image,
          category: selectedDesign.category,
          colors: selectedDesign.colors || [],
          sizes: selectedDesign.sizes || [],
          moq: selectedDesign.moq,
          leadTime: selectedDesign.leadTime,
        },
        enquiryDetails: {
          quantity,
          selectedColor: selectedColor || null,
          selectedSize: selectedSize || null,
          questions: selectedQuestions,
          customAnswers,
          notes,
        },
        // 🔔 Add notification flag for manufacturer
        isEnquiry: true,
        status: 'pending',
      });

      setIsSuccess(true);
      
      // Close after success
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
        if (onEnquirySent) onEnquirySent();
      }, 2000);

    } catch (error) {
      console.error('Error sending enquiry:', error);
      setError('Failed to send enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedDesign(null);
    setSelectedQuestions([]);
    setCustomAnswers({});
    setQuantity(50);
    setSelectedColor(null);
    setSelectedSize(null);
    setNotes('');
    setError(null);
    setIsSuccess(false);
  };

  // ============================================================================
  // CLOSE HANDLER
  // ============================================================================
  
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // ============================================================================
  // EARLY RETURN
  // ============================================================================
  
  if (!isOpen) return null;

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center p-0 lg:p-6">
      
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full h-full lg:max-w-4xl lg:h-[90vh] bg-black lg:border lg:border-zinc-800 flex flex-col overflow-hidden animate-in slide-in-from-bottom lg:zoom-in-95 duration-300">
        
        {/* ========== HEADER ========== */}
        <div className="p-6 border-b border-zinc-800 flex justify-between items-center shrink-0 bg-black">
          <div>
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">
              Enquire About Production
            </h2>
            <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mt-1">
              {manufacturer?.businessName || manufacturer?.displayName || 'Manufacturer'}
            </p>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 bg-zinc-900 rounded-full hover:bg-white hover:text-black transition-colors text-zinc-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* ========== CONTENT ========== */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-8 pb-32 scrollbar-hide">
          
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-950/20 border border-red-900/50 rounded-xl flex items-center gap-3 text-red-400 text-sm">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {isSuccess && (
            <div className="p-4 bg-green-950/20 border border-green-900/50 rounded-xl flex items-center gap-3 text-green-400 text-sm">
              <CheckCircle size={20} />
              <span>Enquiry sent successfully! The manufacturer will respond shortly.</span>
            </div>
          )}

          {!isSuccess && (
            <>
              {/* ===== STEP 1: SELECT DESIGN ===== */}
              <div>
                <div className="flex items-center gap-3 mb-4 border-b border-zinc-800 pb-3">
                  <FileText size={18} className="text-zinc-500" />
                  <h3 className="text-[10px] font-black text-white uppercase tracking-widest">
                    1. Select Design
                  </h3>
                </div>

                {loadingDesigns ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={24} className="animate-spin text-zinc-500" />
                  </div>
                ) : designs.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-zinc-800 rounded-xl">
                    <ImageIcon size={32} className="mx-auto text-zinc-600 mb-3" />
                    <p className="text-zinc-500 font-medium text-sm">No designs found</p>
                    <p className="text-zinc-600 text-xs mt-1">
                      Create a design in <span className="text-white font-bold">Scoop Studio</span> first
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {designs.map((design) => (
                      <button
                        key={design.id}
                        onClick={() => setSelectedDesign(design)}
                        className={`relative group text-left border-2 rounded-xl overflow-hidden transition-all ${
                          selectedDesign?.id === design.id
                            ? 'border-white bg-zinc-900'
                            : 'border-zinc-800 hover:border-zinc-600 bg-zinc-950'
                        }`}
                      >
                        <div className="aspect-square bg-black overflow-hidden">
                          <img 
                            src={design.image} 
                            alt={design.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-3">
                          <p className="text-xs font-bold text-white truncate">{design.name}</p>
                          <p className="text-[9px] text-zinc-500 uppercase tracking-widest">
                            {design.category || 'Design'}
                          </p>
                          {selectedDesign?.id === design.id && (
                            <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                              <CheckCircle size={12} className="text-black" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* ===== STEP 2: QUICK QUESTIONS ===== */}
              {selectedDesign && (
                <div>
                  <div className="flex items-center gap-3 mb-4 border-b border-zinc-800 pb-3">
                    <MessageSquare size={18} className="text-zinc-500" />
                    <h3 className="text-[10px] font-black text-white uppercase tracking-widest">
                      2. Quick Questions
                    </h3>
                    <span className="text-[8px] text-zinc-500 font-mono">
                      Click to add to enquiry
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {QUICK_QUESTIONS.map((q) => (
                      <button
                        key={q.id}
                        onClick={() => toggleQuestion(q.question)}
                        className={`px-3 py-2 rounded-full border text-[9px] font-bold uppercase tracking-widest transition-all ${
                          selectedQuestions.includes(q.question)
                            ? 'bg-white text-black border-white'
                            : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-white'
                        }`}
                      >
                        {q.defaultText || q.question}
                      </button>
                    ))}
                  </div>

                  {/* Custom answers for selected questions */}
                  {selectedQuestions.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <p className="text-[9px] text-zinc-500 uppercase tracking-widest font-bold">
                        Your Questions:
                      </p>
                      {selectedQuestions.map((q) => {
                        const template = QUICK_QUESTIONS.find(t => t.question === q);
                        if (!template) return null;
                        return (
                          <div key={template.id} className="flex items-center gap-3">
                            <span className="text-[10px] text-white font-medium flex-1">{q}</span>
                            <input
                              type="text"
                              placeholder="Your answer..."
                              value={customAnswers[template.id] || ''}
                              onChange={(e) => handleCustomAnswerChange(template.id, e.target.value)}
                              className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white transition-colors"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ===== STEP 3: ORDER DETAILS ===== */}
              {selectedDesign && (
                <div>
                  <div className="flex items-center gap-3 mb-4 border-b border-zinc-800 pb-3">
                    <ShoppingBag size={18} className="text-zinc-500" />
                    <h3 className="text-[10px] font-black text-white uppercase tracking-widest">
                      3. Order Details
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Quantity */}
                    <div>
                      <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">
                        Quantity (units)
                      </label>
                      <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 10))}
                          className="px-4 py-3 hover:bg-zinc-800 transition-colors text-zinc-400"
                        >
                          <Minus size={16} />
                        </button>
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full bg-transparent text-center text-white font-bold text-lg outline-none"
                          min="1"
                        />
                        <button
                          onClick={() => setQuantity(quantity + 10)}
                          className="px-4 py-3 hover:bg-zinc-800 transition-colors text-zinc-400"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      {selectedDesign.moq && quantity < selectedDesign.moq && (
                        <p className="text-[9px] text-orange-500 mt-1 font-bold uppercase tracking-widest">
                          ⚠️ MOQ is {selectedDesign.moq} units
                        </p>
                      )}
                    </div>

                    {/* Colors */}
                    {selectedDesign.colors && selectedDesign.colors.length > 0 && (
                      <div>
                        <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">
                          Color
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedDesign.colors.map((color) => (
                            <button
                              key={color}
                              onClick={() => setSelectedColor(selectedColor === color ? null : color)}
                              className={`px-3 py-1.5 rounded-lg border text-[9px] font-bold uppercase transition-all ${
                                selectedColor === color
                                  ? 'bg-white text-black border-white'
                                  : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-white'
                              }`}
                            >
                              {color}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sizes */}
                    {selectedDesign.sizes && selectedDesign.sizes.length > 0 && (
                      <div>
                        <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">
                          Size
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedDesign.sizes.map((size) => (
                            <button
                              key={size}
                              onClick={() => setSelectedSize(selectedSize === size ? null : size)}
                              className={`px-3 py-1.5 rounded-lg border text-[9px] font-bold uppercase transition-all ${
                                selectedSize === size
                                  ? 'bg-white text-black border-white'
                                  : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-white'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="mt-4">
                    <label className="block text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any special requirements, deadlines, or additional info..."
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white transition-colors resize-none h-20"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ========== FOOTER ========== */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-zinc-800 bg-black shrink-0">
          <button
            onClick={handleSendEnquiry}
            disabled={isSubmitting || isSuccess || !selectedDesign}
            className="w-full py-4 bg-white text-black font-black uppercase tracking-[0.2em] text-xs rounded-full lg:rounded-none flex items-center justify-center gap-3 hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_30px_rgba(255,255,255,0.1)]"
          >
            {isSubmitting ? (
              <Loader2 size={20} className="animate-spin" />
            ) : isSuccess ? (
              <CheckCircle size={20} />
            ) : (
              <Send size={18} />
            )}
            {isSubmitting ? 'Sending...' : isSuccess ? 'Sent!' : 'Send Enquiry'}
          </button>
          {!selectedDesign && !isSuccess && (
            <p className="text-[9px] text-zinc-500 text-center mt-2 font-mono uppercase tracking-widest">
              Select a design to enable enquiry
            </p>
          )}
        </div>

      </div>
    </div>
  );
};

export default EnquiryModal;